"use client";

import React, { useRef } from "react";
import type { AriaPopoverProps } from "react-aria";
import {
  DismissButton,
  Overlay,
  useButton,
  useOverlayTrigger,
  usePopover,
} from "react-aria";
import {
  OverlayTriggerProps,
  useOverlayTriggerState,
  type OverlayTriggerState,
} from "react-stately";
import { csx } from "../utils";
import { useTheme } from "./ThemeProvider";

import "./Popover.css";

/**
 * Hook for creating a popover trigger. This hook manages the state and events for the popover
 * and popover trigger. For examples see Popover.stories.tsx.
 */

export function usePopoverTrigger(props?: OverlayTriggerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);

  const state = useOverlayTriggerState(props || {});
  const { triggerProps, overlayProps } = useOverlayTrigger(
    { type: "dialog" },
    state,
    triggerRef
  );
  const { buttonProps } = useButton(triggerProps, triggerRef);

  return { buttonProps, overlayProps, buttonRef: triggerRef, state };
}

interface PopoverProps extends Omit<AriaPopoverProps, "popoverRef"> {
  /**
   * Content to display within the popover.
   */
  children: React.ReactNode;
  /**
   * The `isOpen` state of the popover and the methods to toggle it.
   */
  state: OverlayTriggerState;
  /**
   * Can optionally forward the ref to the popover.
   */
  popoverRef?: React.RefObject<HTMLDivElement>;
  /**
   * Whether to show an arrow on the popover.
   */
  arrow?: boolean;
}

/**
 * Popover is an unstyled popover component that handles positioning. It should be used
 * with the Dialog component to provide a styled popover.
 *
 * A popover displays interactive content in context with a trigger element.
 * A tooltip displays a description of an element on hover or focus.
 *
 * @interface PopoverProps
 */
export function Popover({
  children,
  state,
  arrow = false,
  offset,
  ...props
}: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { popoverRef = ref } = props;
  const { className, style } = useTheme();

  let { popoverProps, underlayProps, arrowProps, placement } = usePopover(
    {
      ...props,
      offset,
      popoverRef,
    },
    state
  );

  return (
    <Overlay>
      <div {...underlayProps} style={{ position: "fixed", inset: 0 }} />
      <div
        {...popoverProps}
        ref={popoverRef}
        className={csx("proton-Popover", className)}
        style={{ ...popoverProps.style, ...style }}
        data-testid={props["data-testid"] || undefined}
        data-placement={placement}
        data-entering={true}
      >
        <DismissButton onDismiss={state.close} />
        {arrow && (
          <svg
            {...arrowProps}
            className="arrow"
            data-placement={placement}
            viewBox="0 0 12 12"
          >
            <title>arrow</title>
            <path d="M0 0 L6 6 L12 0" />
          </svg>
        )}
        {children}
      </div>
    </Overlay>
  );
}

export default Popover;
